import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  AlertTriangle,
  ArrowLeft,
  Download,
  FolderOpen,
  Plus,
  Ruler,
  ShieldOff,
  Trash2,
  Upload,
  Wifi,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/app/components/ui/Button'
import { Surface } from '@/app/components/ui/Surface'
import { calibrationImage, calibrationSheet } from '@/app/lib/print'
import { MAX_INSET_IN, parseInset } from '@/core/print'
import { forgetToken, verifyAccount } from '@/app/lib/raia'
import { probeVideoSupport, type VideoSupport } from '@/app/lib/video'
import { encoderReady, ffmpegState, preload, subscribeFfmpeg, type FfmpegState } from '@/app/lib/ffmpeg'
import {
  deleteLayout,
  exportAll,
  importBackup,
  importLegacy,
  layoutPreview,
  listLayouts,
  type StoredLayout,
} from '@/app/lib/local/layouts'
import {
  checkPermission,
  describeDirectory,
  ensurePermission,
  pickDirectory,
  type DirectorySummary,
} from '@/app/lib/local/fs'
import { listJobs, onQueueEvent, queuedBytes, wake } from '@/app/lib/local/queue'
import {
  DEFAULT_SETTINGS,
  RAIA_ACCOUNT_EMAIL,
  RAIA_API_BASE_URL,
  clearCredentials,
} from '@/app/lib/local/settings'
import { useAppStore } from '@/app/store/useAppStore'
import { cn } from '@/app/lib/cn'

/**
 * Configuration, deliberately outside the guided flow.
 *
 * This is set up once when the booth is built and then not touched all evening,
 * so it does not belong in the per-guest path. Grouping it by question — where
 * do photos come from, where do they go, how are they printed — beats the
 * desktop app's single column of unlabelled buttons.
 *
 * Three sections changed shape when the agent went away. The printer dropdown
 * is gone, because the browser's print dialog chooses the destination; in its
 * place is the print inset, which is the number that decides whether a 4R sheet
 * comes out edge-to-edge and the only part of the old print geometry an
 * operator now has to set themselves. The FFmpeg installer is gone, because
 * WebCodecs needs no install. And layouts are imported and exported here rather
 * than shared through a file on disk with the desktop app.
 */
export function SettingsScreen() {
  const navigate = useNavigate()
  const { settings, monitored, target, chooseMonitored, chooseTarget, updateSettings } =
    useAppStore()

  const [layouts, setLayouts] = useState<StoredLayout[]>([])
  const [previews, setPreviews] = useState<Record<string, string>>({})
  // Mirrors `previews` so the unmount cleanup can reach the latest set without
  // re-subscribing on every render.
  const previewsRef = useRef<Record<string, string>>({})
  const [pending, setPending] = useState(0)
  const [verifying, setVerifying] = useState(false)
  const [video, setVideo] = useState<VideoSupport | null>(null)
  const [ffmpeg, setFfmpeg] = useState<FfmpegState>(ffmpegState)
  /** Two-step delete. Holds the layout id awaiting confirmation. */
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  /**
   * Whether each granted folder is still readable.
   *
   * A stored handle survives a browser restart but its permission does not, and
   * the row showed only the folder's NAME — so Settings looked correct while the
   * watcher could not open anything.
   */
  const [grants, setGrants] = useState<{ monitored: boolean; target: boolean }>({
    monitored: true,
    target: true,
  })
  /**
   * What is inside each chosen folder.
   *
   * Standing in for the full path, which the File System Access API does not
   * expose — a handle carries `name` and nothing else. See `describeDirectory`.
   */
  const [contents, setContents] = useState<{
    monitored: DirectorySummary | null
    target: DirectorySummary | null
  }>({ monitored: null, target: null })

  const loadLayouts = async () => {
    const all = await listLayouts().catch(() => [])
    setLayouts(all)

    const urls: Record<string, string> = {}
    for (const layout of all) {
      const blob = await layoutPreview(layout)
      if (blob) urls[layout.id] = URL.createObjectURL(blob)
    }
    setPreviews((current) => {
      // Release the previous batch rather than leaking one object URL per visit
      // to this screen.
      for (const url of Object.values(current)) URL.revokeObjectURL(url)
      previewsRef.current = urls
      return urls
    })
  }

  useEffect(() => {
    void loadLayouts()
    void queuedBytes().then(setPending)
    // Memoised in lib/video.ts, so visiting Settings does not re-encode a probe
    // frame every time.
    void probeVideoSupport().then(setVideo)
    // A previous visit — or a previous evening — may already have fetched the
    // encoder; the service worker keeps it, so report it as done rather than
    // offering a download that would be a no-op.
    void encoderReady().then((ready) => {
      if (ready) {
        setFfmpeg((current) =>
          current.status === 'idle' ? { status: 'ready', progress: 1 } : current,
        )
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => subscribeFfmpeg(setFfmpeg), [])

  // The queue drains in the background, so a figure read once on mount goes
  // stale while the operator is looking straight at it.
  useEffect(() => onQueueEvent(() => void queuedBytes().then(setPending)), [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const [m, t] = await Promise.all([
        monitored ? checkPermission(monitored, 'read') : Promise.resolve('granted' as const),
        target ? checkPermission(target, 'readwrite') : Promise.resolve('granted' as const),
      ])
      if (cancelled) return
      setGrants({ monitored: m === 'granted', target: t === 'granted' })

      // Only walk a folder the browser will actually let us read; without the
      // permission every `values()` throws and the row would just look empty.
      const [mc, tc] = await Promise.all([
        monitored && m === 'granted' ? describeDirectory(monitored).catch(() => null) : null,
        target && t === 'granted' ? describeDirectory(target).catch(() => null) : null,
      ])
      if (!cancelled) setContents({ monitored: mc, target: tc })
    })()
    return () => {
      cancelled = true
    }
  }, [monitored, target])

  // Object URLs for the layout previews outlive this screen otherwise: they were
  // only released when `loadLayouts` ran again, so navigating away leaked one
  // per layout, every visit, all evening.
  useEffect(
    () => () => {
      for (const url of Object.values(previewsRef.current)) URL.revokeObjectURL(url)
    },
    [],
  )

  const patch = async (values: Parameters<typeof updateSettings>[0]) => {
    try {
      await updateSettings(values)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal menyimpan')
    }
  }

  const guard = async (fn: () => Promise<unknown>, message: string) => {
    try {
      await fn()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : message)
    }
  }

  const testAccount = async () => {
    if (!settings) return
    setVerifying(true)
    try {
      await verifyAccount({
        baseUrl: settings.apiBaseUrl,
        email: settings.apiEmail,
        password: settings.apiPassword,
      })
      toast.success('Akun Raia terhubung')
      // Anything queued while the account was wrong should go now.
      wake()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Akun Raia tidak bisa dipakai')
    } finally {
      setVerifying(false)
    }
  }

  const downloadEncoder = () =>
    guard(async () => {
      await preload()
      toast.success('Encoder cadangan siap')
    }, 'Tidak bisa mengunduh encoder cadangan')

  const printCalibration = () =>
    guard(async () => {
      await calibrationSheet(await calibrationImage())
    }, 'Tidak bisa mencetak lembar kalibrasi')

  const importFromDesktop = () =>
    guard(async () => {
      const folder = await pickDirectory('citaria-layouts-import', 'read')
      if (!folder) return

      // layouts.json and the frames it references normally sit in the same
      // folder — %LOCALAPPDATA%\CitariaApp — so one grant covers both.
      let json: string
      try {
        json = await (await (await folder.getFileHandle('layouts.json')).getFile()).text()
      } catch {
        throw new Error('layouts.json tidak ada di folder itu')
      }

      const { imported, missing } = await importLegacy(json, folder)
      await loadLayouts()

      if (imported.length > 0) toast.success(`${imported.length} layout diimpor`)
      if (missing.length > 0) {
        toast.warning(`${missing.length} layout dilewati — gambar latarnya tidak ditemukan`)
      }
    }, 'Tidak bisa mengimpor layouts.json')

  const exportLayouts = () =>
    guard(async () => {
      const blob = await exportAll()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'citaria-layouts.json'
      link.click()
      // Revoked on the next turn, not immediately: the download reads the blob
      // asynchronously after click(), and pulling the URL out from under it can
      // hand the operator an empty file. These backups inline every background
      // image, so they are large enough for the race to be real.
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    }, 'Tidak bisa mengekspor layout')

  /**
   * Re-grants a folder whose permission lapsed.
   *
   * Chrome drops a stored handle to "prompt" when the browser closes and only
   * restores it from a user gesture, which is why this is a button.
   */
  const regrant = (which: 'monitored' | 'target') =>
    guard(async () => {
      const handle = which === 'monitored' ? monitored : target
      if (!handle) return
      const ok = await ensurePermission(handle, which === 'target' ? 'readwrite' : 'read')
      setGrants((current) => ({ ...current, [which]: ok }))

      if (!ok) {
        toast.error('Izin folder ditolak')
        return
      }
      // The contents could not be read while the permission was gone, so the
      // row was blank; now that it is back, fill it in.
      const summary = await describeDirectory(handle).catch(() => null)
      setContents((current) => ({ ...current, [which]: summary }))
      toast.success('Folder bisa diakses lagi')
    }, 'Tidak bisa meminta izin folder')

  /**
   * Clears the Raia account from this machine.
   *
   * §7: the credentials live in IndexedDB rather than a file only the Windows
   * user could read, and an event PC that other people use should be signed out
   * at the end of the night. There was no way to do that — `clearCredentials`
   * existed and nothing called it.
   *
   * Queued sessions are deliberately left alone: they are a paying customer's
   * photos, and they upload as soon as an account is entered again.
   */
  const signOut = () =>
    guard(async () => {
      await clearCredentials()
      forgetToken()
      await useAppStore.getState().load()
      toast.success('Akun Raia dihapus dari PC ini')
    }, 'Tidak bisa menghapus akun')

  const restoreLayouts = () =>
    guard(async () => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'application/json'
      const chosen = await new Promise<File | null>((resolve) => {
        input.onchange = () => resolve(input.files?.[0] ?? null)
        input.oncancel = () => resolve(null)
        input.click()
      })
      if (!chosen) return

      const restored = await importBackup(await chosen.text())
      await loadLayouts()
      toast.success(`${restored.length} layout dipulihkan`)
    }, 'Tidak bisa memulihkan cadangan')

  const removeLayout = (id: string) =>
    guard(async () => {
      await deleteLayout(id)
      await loadLayouts()
    }, 'Gagal menghapus layout')

  const raiaReady = Boolean(settings?.apiEmail && settings?.apiPassword)

  return (
    <div className="citaria-step-enter mx-auto flex min-h-0 w-full max-w-[1500px] flex-1 flex-col">
      <header className="flex items-center gap-3 px-5 pb-4 pt-4 sm:px-8">
        <Button variant="ghost" onClick={() => navigate('/')} className="w-[48px] px-0">
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-[22px] font-semibold tracking-tight">Pengaturan</h1>
        <div className="flex-1" />
        {!monitored && (
          <span className="rounded-full bg-warn/15 px-3 py-1 text-[13px] text-warn">
            Belum lengkap
          </span>
        )}
      </header>

      {/*
        Two columns from xl up, one below. The single 900px column left most of a
        1366x768 event PC empty while the sections it did show ran three screens
        deep — and this is a screen an operator works through in order while the
        booth is being built, so scrolling past it repeatedly is the cost.

        The split is by subject, not by height: the left column is the booth
        (where photos come from, where they are archived, what frame they print
        onto) and the right is what happens to a session (how it prints, the
        video, where it uploads, what is still queued).

        ONE scroll container around both, not one per column — two independent
        scrollbars is the thing that was just fixed on the designer.
      */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-8 sm:px-8">
        <div className="grid items-start gap-4 xl:grid-cols-2">
          <div className="space-y-4">
            <Section title="Sumber foto" hint="Folder tempat kamera menyimpan hasil jepretan.">
              <PathRow
                label="Folder pantau"
                value={monitored?.name}
                onPick={() => guard(chooseMonitored, 'Tidak bisa membuka folder')}
              />
              <GrantWarning
                show={Boolean(monitored) && !grants.monitored}
                onRegrant={() => regrant('monitored')}
              />
              <FolderContents summary={contents.monitored} kind="photos" />
              <p className="text-[13px] text-ink-muted">
                Browser tidak memberi tahu path lengkap folder — yang tampil di atas adalah nama
                folder beserta isinya, supaya bisa dipastikan ini folder yang benar. Izin folder
                juga dilepas browser setiap kali ditutup, jadi diminta lagi di awal setiap acara.
              </p>
            </Section>

            <Section
              title="Arsip lokal"
              hint="Salinan sesi di PC ini. Wajib diatur sebelum acara dimulai."
            >
              <PathRow
                label="Folder simpan"
                value={target?.name}
                onPick={() => guard(chooseTarget, 'Tidak bisa membuka folder')}
              />
              <GrantWarning
                show={Boolean(target) && !grants.target}
                onRegrant={() => regrant('target')}
              />
              <FolderContents summary={contents.target} kind="sessions" />
              {/*
                The agent build warned here when the folder was outside "My Drive",
                because that folder WAS how photos reached the guest. It is not any
                more — raia-api receives them directly — so any folder will do, and
                an empty one only costs the operator their local copy.
              */}
            </Section>

            <Section
              title="Layout"
              hint="Bingkai cetak. Disimpan di browser ini — ekspor untuk memindahkannya."
              action={
                <Button variant="secondary" onClick={() => navigate('/layouts/new')}>
                  <Plus size={18} />
                  Buat layout
                </Button>
              }
            >
              {layouts.length === 0 ? (
                <p className="rounded-[12px] border border-dashed border-line px-4 py-8 text-center text-[14px] text-ink-muted">
                  Belum ada layout. Tanpa layout, mencetak tidak bisa dilakukan.
                </p>
              ) : (
                <div
                  className="grid gap-3"
                  style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}
                >
                  {layouts.map((layout) => (
                    <div
                      key={layout.id}
                      className="overflow-hidden rounded-[14px] bg-raised ring-1 ring-line"
                    >
                      <button
                        type="button"
                        onClick={() => navigate(`/layouts/${layout.id}`)}
                        className="block w-full"
                      >
                        <div className="grid aspect-[2/3] place-items-center bg-black/30">
                          {previews[layout.id] ? (
                            <img
                              src={previews[layout.id]}
                              alt=""
                              className="max-h-full max-w-full object-contain"
                              draggable={false}
                            />
                          ) : (
                            <span className="text-[13px] text-ink-faint">Tanpa pratinjau</span>
                          )}
                        </div>
                      </button>
                      <div className="flex items-center justify-between gap-2 px-3 py-2">
                        <span className="min-w-0 flex-1">
                          {/* The name is optional and older layouts have none, so
                              the slot count stays the fallback identity. */}
                          <span className="block truncate text-[13px] text-ink" title={layout.name}>
                            {layout.name || `${layout.slots.length} slot`}
                          </span>
                          {layout.name && (
                            <span className="block text-[11px] text-ink-faint">
                              {layout.slots.length} slot
                            </span>
                          )}
                        </span>
                        {/*
                          Two-step, because deleting takes the layout, its
                          background, its overlay and its preview with it and there
                          is no undo — an operator's frame artwork can be the only
                          copy on the machine. Inline rather than `confirm()`: this
                          runs fullscreen on a touch monitor where a native dialog
                          is both ugly and, in some kiosk setups, suppressed.
                        */}
                        {confirmDelete === layout.id ? (
                          <span className="flex shrink-0 items-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                setConfirmDelete(null)
                                void removeLayout(layout.id)
                              }}
                              className="min-h-[36px] rounded-[8px] bg-danger px-2 text-[12px] font-semibold text-black"
                            >
                              Hapus
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(null)}
                              className="min-h-[36px] rounded-[8px] px-2 text-[12px] text-ink-muted ring-1 ring-line"
                            >
                              Batal
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            aria-label="Hapus layout"
                            onClick={() => setConfirmDelete(layout.id)}
                            className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-ink-faint active:bg-danger/15 active:text-danger"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="secondary" onClick={importFromDesktop}>
                  <Upload size={16} className="mr-2" />
                  Impor layouts.json
                </Button>
                <Button variant="secondary" onClick={restoreLayouts}>
                  <Upload size={16} className="mr-2" />
                  Pulihkan cadangan
                </Button>
                <Button variant="secondary" onClick={exportLayouts} disabled={layouts.length === 0}>
                  <Download size={16} className="mr-2" />
                  Ekspor cadangan
                </Button>
              </div>
              <p className="text-[13px] text-ink-muted">
                <strong>Impor layouts.json</strong> membaca layout dari aplikasi desktop lama: pilih
                folder yang berisi <code>layouts.json</code> beserta gambar-gambar latarnya.
              </p>
            </Section>
          </div>

          <div className="space-y-4">
            {/*
              The printer dropdown was here. It is gone with the print helper: the
              browser's own dialog picks the destination, and the console has no way
              to enumerate printers or report why one is unavailable.
            */}
            <Section
              title="Cetak"
              hint="Kertas 4R. Printer dipilih di dialog cetak browser."
            >
              <div className="rounded-[12px] bg-raised p-4 ring-1 ring-line">
                <p className="text-[14px]">Bleed / margin cetak</p>
                <p className="mt-0.5 text-[13px] text-ink-muted">
                  Nilai positif menyisakan margin putih; nilai negatif melebihkan gambar sampai ke
                  tepi kertas. Cetak lembar kalibrasi, lihat panel mana yang pas, lalu isi angkanya.
                  Boleh pakai koma. Maksimal {MAX_INSET_IN} inci.
                </p>

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <NumberRow
                    label="Kiri / kanan (inci)"
                    value={settings?.printInsetX ?? DEFAULT_SETTINGS.printInsetX}
                    onCommit={(v) => patch({ printInsetX: v })}
                  />
                  <NumberRow
                    label="Atas / bawah (inci)"
                    value={settings?.printInsetY ?? DEFAULT_SETTINGS.printInsetY}
                    onCommit={(v) => patch({ printInsetY: v })}
                  />
                </div>

                <Button variant="secondary" className="mt-3" onClick={printCalibration}>
                  <Ruler size={16} className="mr-2" />
                  Cetak lembar kalibrasi
                </Button>
              </div>

              <Toggle
                label="Balik orientasi"
                hint="Untuk printer yang melaporkan media terputar."
                checked={settings?.invertOrientation ?? false}
                onChange={(v) => patch({ invertOrientation: v })}
              />

              <Toggle
                label="Arsip tanpa QR"
                hint="Salinan yang diterima tamu dirender sebelum QR digambar."
                checked={settings?.archiveWithoutQr ?? false}
                onChange={(v) => patch({ archiveWithoutQr: v })}
              />
            </Section>

            <Section
              title="Video sesi"
              hint="animate.mp4 dikodekan H.264 di browser. Tidak ada yang perlu dipasang."
            >
              <Toggle
                label="Buat animasi sesi"
                hint={
                  video?.supported
                    ? 'Dibuat setelah cetak, tidak menahan antrean tamu.'
                    : (video?.reason ?? 'Memeriksa dukungan encoder…')
                }
                checked={settings?.videoEnabled ?? true}
                // Reflect the SETTING, not the capability. Forcing the switch off on an
                // unsupported machine made it show the opposite of what was stored,
                // so tapping it wrote the wrong value; disabling says the same thing
                // without lying about the state.
                disabled={video ? !video.supported : false}
                onChange={(v) => patch({ videoEnabled: v })}
              />

              {video && !video.supported && (
                <p className="text-[13px] text-ink-muted">
                  PC ini tidak bisa membuat animate.mp4. Matikan "Buat animate.mp4" agar konsol
                  bisa dipakai tanpa video sesi.
                </p>
              )}

              {/*
                The encoder is FFmpeg compiled to WebAssembly, so every machine
                needs the same 32MB — see lib/video.ts for why that beats the
                platform's own H.264. It is required before step 1 will start
                watching, for the same reason it was always front-loaded: the
                alternative is a 32MB download starting with a guest at the booth.
              */}
              {video?.supported && (
                <div className="rounded-[12px] bg-raised p-4 ring-1 ring-line">
                  <p className="text-[14px]">Encoder video</p>
                  <p className="mt-0.5 text-[13px] text-ink-muted">
                    FFmpeg dijalankan di browser (32 MB, sekali saja per PC). Setelah diunduh,
                    video tetap bisa dibuat walau internet venue mati.
                  </p>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Button
                      variant="secondary"
                      onClick={downloadEncoder}
                      disabled={ffmpeg.status === 'loading' || ffmpeg.status === 'ready'}
                    >
                      <Download size={16} className="mr-2" />
                      {ffmpeg.status === 'ready'
                        ? 'Sudah diunduh'
                        : ffmpeg.status === 'loading'
                          ? `Mengunduh… ${Math.round(ffmpeg.progress * 100)}%`
                          : 'Unduh encoder'}
                    </Button>

                    {ffmpeg.status === 'error' && (
                      <span className="text-[13px] text-warn">{ffmpeg.error}</span>
                    )}
                  </div>
                </div>
              )}
            </Section>

            <Section
              title="Akun Raia"
              hint="Akun bersama untuk semua PC acara. Cukup isi passwordnya."
            >
              {/*
                Fixed, not typed in: one account serves the whole operation, so
                asking each operator for it bought nothing and was a typo away
                from a console that silently never uploads. The password stays
                theirs to enter — see the note in local/settings.ts about which
                half of §7 this does and does not walk back.
              */}
              <ReadOnlyRow label="API base URL" value={settings?.apiBaseUrl ?? RAIA_API_BASE_URL} />
              <ReadOnlyRow label="Email" value={settings?.apiEmail ?? RAIA_ACCOUNT_EMAIL} />
              <TextRow
                label="Password"
                type="password"
                value={settings?.apiPassword}
                onCommit={(v) => patch({ apiPassword: v })}
              />

              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={testAccount} disabled={!raiaReady || verifying}>
                  <Wifi size={16} className="mr-2" />
                  {verifying ? 'Menguji…' : 'Uji koneksi'}
                </Button>

                {/*
                  §7: these credentials sit in IndexedDB on a machine that is often
                  borrowed for the night. Being able to remove them is the other half
                  of being able to enter them.
                */}
                {raiaReady && (
                  <Button variant="secondary" onClick={signOut}>
                    <ShieldOff size={16} className="mr-2" />
                    Keluar
                  </Button>
                )}
              </div>

              {!raiaReady && (
                <p className="text-[13px] text-ink-muted">
                  Password belum diisi. Tamu tetap menerima cetakan dengan QR yang benar — link
                  itu baru mulai bekerja setelah sesi terunggah.
                </p>
              )}
            </Section>

            {/*
              The queue used to be a service that drained whether or not anyone was
              looking. It is this tab now, so how much is waiting has to be visible
              somewhere the operator can find it.
            */}
            <Section title="Antrean unggah" hint="Sesi yang belum sampai ke Raia.">
              <div className="flex min-h-[56px] flex-wrap items-center gap-3 rounded-[12px] bg-raised px-4 py-2 ring-1 ring-line">
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] text-ink-muted">Menunggu</span>
                  <span className="block text-[14px] text-ink">
                    {pending > 0 ? `${(pending / 1_048_576).toFixed(1)} MB` : 'Kosong'}
                  </span>
                </span>
                <Button
                  variant="secondary"
                  onClick={() =>
                    guard(async () => {
                      wake()
                      const jobs = await listJobs()
                      setPending(await queuedBytes())
                      toast.success(jobs.length > 0 ? `${jobs.length} sesi dicoba lagi` : 'Antrean kosong')
                    }, 'Tidak bisa memuat antrean')
                  }
                >
                  Coba lagi
                </Button>
              </div>
            </Section>

          </div>
        </div>
      </div>
    </div>
  )
}

function Section({
  title,
  hint,
  action,
  children,
}: {
  title: string
  hint?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Surface className="p-5">
      <div className="mb-4 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-[16px] font-semibold">{title}</h2>
          {hint && <p className="mt-0.5 text-[13px] text-ink-muted">{hint}</p>}
        </div>
        {action}
      </div>
      <div className="space-y-3">{children}</div>
    </Surface>
  )
}

function PathRow({
  label,
  value,
  onPick,
}: {
  label: string
  value?: string
  onPick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className="flex min-h-[56px] w-full items-center gap-3 rounded-[12px] bg-raised px-4 text-left ring-1 ring-line active:bg-line"
    >
      <FolderOpen size={18} className="shrink-0 text-ink-muted" />
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] text-ink-muted">{label}</span>
        <span
          className={cn('block truncate text-[14px]', value ? 'text-ink' : 'text-ink-faint')}
          title={value}
        >
          {value || 'Belum dipilih'}
        </span>
      </span>
    </button>
  )
}

function TextRow({
  label,
  value,
  type = 'text',
  placeholder,
  onCommit,
}: {
  label: string
  value?: string
  type?: 'text' | 'email' | 'password'
  placeholder?: string
  onCommit: (value: string) => void
}) {
  // Held locally and written on blur rather than on every keystroke: saving a
  // password one character at a time would also store every prefix of it.
  const [draft, setDraft] = useState(value ?? '')
  useEffect(() => setDraft(value ?? ''), [value])

  return (
    <label className="block rounded-[12px] bg-raised px-4 py-2.5 ring-1 ring-line focus-within:ring-brand">
      <span className="block text-[13px] text-ink-muted">{label}</span>
      <input
        type={type}
        value={draft}
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== (value ?? '')) onCommit(draft)
        }}
        className="min-h-[32px] w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-ink-faint"
      />
    </label>
  )
}

/**
 * A signed inch value, committed on blur.
 *
 * `step` is a hundredth of an inch because that is the unit the print geometry
 * has always been expressed in — 4R is 400x600 hundredths, and the ported
 * offsets were 6 and 4 of them.
 */
function NumberRow({
  label,
  value,
  onCommit,
}: {
  label: string
  value: number
  onCommit: (value: number) => void
}) {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => setDraft(String(value)), [value])

  const invalid = parseInset(draft) === null

  return (
    <label
      className={cn(
        'block rounded-[12px] bg-surface px-4 py-2.5 ring-1 focus-within:ring-brand',
        invalid ? 'ring-danger' : 'ring-line',
      )}
    >
      <span className="block text-[13px] text-ink-muted">{label}</span>
      {/*
        `type="text"`, not `number`. An input[type=number] reports a
        comma-typed value as the empty string, and the console runs in
        Indonesian where a comma IS the decimal separator — so the operator's
        own way of writing 0,06 blanked the field. `parseInset` accepts both.
      */}
      <input
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const parsed = parseInset(draft)
          // Null means "not a number", including empty — which must revert
          // rather than commit zero. Number('') is 0 and finite, so the old
          // isFinite guard let an emptied box silently turn a bleed into an
          // edge-to-edge fill (§9: this is the setting that costs paper).
          if (parsed === null) setDraft(String(value))
          else {
            setDraft(String(parsed))
            if (parsed !== value) onCommit(parsed)
          }
        }}
        className="min-h-[32px] w-full bg-transparent text-[15px] text-ink outline-none"
      />
    </label>
  )
}

function Toggle({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string
  hint?: string
  checked: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'flex min-h-[56px] w-full items-center gap-4 rounded-[12px] bg-raised px-4 text-left ring-1 ring-line',
        disabled && 'opacity-50',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[14px]">{label}</span>
        {hint && <span className="block text-[12px] text-ink-muted">{hint}</span>}
      </span>
      <span
        aria-hidden
        className={cn(
          'relative h-[30px] w-[52px] shrink-0 rounded-full transition-colors',
          checked ? 'bg-brand' : 'bg-line-strong',
        )}
      >
        <span
          className={cn(
            'absolute top-[3px] h-6 w-6 rounded-full bg-white transition-[left]',
            checked ? 'left-[25px]' : 'left-[3px]',
          )}
        />
      </span>
    </button>
  )
}

/**
 * Shown when a chosen folder is no longer readable.
 *
 * The row above it displays the folder's NAME, which survives a browser restart
 * — the permission does not. Without this the screen looked correctly
 * configured while the watcher could not open a single file.
 */
function GrantWarning({ show, onRegrant }: { show: boolean; onRegrant: () => void }) {
  if (!show) return null

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[12px] bg-warn/10 p-3 text-[13px] text-warn ring-1 ring-warn/30">
      <AlertTriangle size={16} className="shrink-0" />
      <span className="min-w-0 flex-1">Izin folder ini sudah dilepas browser.</span>
      <Button variant="secondary" onClick={onRegrant}>
        Izinkan lagi
      </Button>
    </div>
  )
}

/**
 * A setting the operator can see but not change.
 *
 * Rendered as plain text rather than a disabled input: a greyed-out box invites
 * a tap and then refuses it, which reads as broken. This just states the value.
 */
function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[12px] bg-surface px-4 py-2.5 ring-1 ring-line">
      <span className="block text-[13px] text-ink-muted">{label}</span>
      <span className="block truncate text-[15px] text-ink-muted" title={value}>
        {value}
      </span>
    </div>
  )
}

/**
 * What is actually inside a chosen folder.
 *
 * This stands in for the full path, which cannot be shown: a
 * `FileSystemDirectoryHandle` carries `name` and nothing else — no path
 * property, and `resolve()` only works relative to an ancestor handle the page
 * already holds. The browser never tells the page where the folder is.
 *
 * Contents answer the real question better anyway. Two folders called "photos"
 * look alike in a path listing too; "482 foto · IMG_0431.JPG, IMG_0432.JPG"
 * proves the camera is writing into this one.
 */
function FolderContents({
  summary,
  kind,
}: {
  summary: DirectorySummary | null
  kind: 'photos' | 'sessions'
}) {
  if (!summary) return null

  const atLeast = summary.truncated ? 'minimal ' : ''

  if (kind === 'sessions') {
    return (
      <p className="text-[13px] text-ink-muted">
        Berisi {atLeast}
        {summary.folders} folder sesi.
      </p>
    )
  }

  if (summary.images === 0) {
    return (
      <p className="text-[13px] text-warn">
        Tidak ada foto di folder ini. Pastikan ini folder tempat kamera menyimpan.
      </p>
    )
  }

  return (
    <p className="truncate text-[13px] text-ink-muted" title={summary.sample.join(', ')}>
      {atLeast}
      {summary.images} foto{summary.sample.length > 0 && ` · ${summary.sample.join(', ')}`}
    </p>
  )
}
